import { styled, Typography, type TypographyProps } from '@mui/material';

import { PropsWithChildren } from 'react';
import { useSuggestions } from '~/hooks/useSuggestions';
import { Searches } from '~/types/searches';
import { Suggestions } from '~/types/suggestions';
interface ReportNameProps {
  search: Searches.StoredSearchObject;
  TypographyProps: TypographyProps;
}
export const ReportName = ({ search, TypographyProps }: ReportNameProps) => {
  const suggestions = useSuggestions();
  const hasAll = search.all.length > 0;
  const hasAny = search.any.length > 0;
  const hasNone = search.none.length > 0;

  return (
    <Container>
      <OperatorSection
        items={search.all}
        conjunction="and"
        suggestions={suggestions}
        {...TypographyProps}
      />

      {hasAll && hasAny && (
        <ConjunctionText {...TypographyProps}> and </ConjunctionText>
      )}

      <OperatorSection
        items={search.any}
        conjunction="or"
        suggestions={suggestions}
        {...TypographyProps}
      />

      {(hasAll || hasAny) && hasNone && (
        <ConjunctionText {...TypographyProps}> and </ConjunctionText>
      )}

      {hasNone && <ConjunctionText {...TypographyProps}> not </ConjunctionText>}
      <OperatorSection
        items={search.none}
        conjunction="nor"
        suggestions={suggestions}
        {...TypographyProps}
      />
    </Container>
  );
};

interface OperatorSectionProps extends TypographyProps {
  items: Searches.IStoredSearchItem[];
  conjunction: string;
  suggestions: Suggestions.Suggestion[];
}
const OperatorSection = ({
  items,
  conjunction,
  suggestions,
  ...TypographyProps
}: OperatorSectionProps) => {
  return (
    <>
      {items.length > 1 && (
        <ConjunctionText {...TypographyProps}>(</ConjunctionText>
      )}
      {items.map((item, index) => {
        const { type, value } = item;
        return (
          <OperatorValue
            index={index}
            key={index}
            type={type}
            value={
              type === 'code' ? value : getReportNameByUuid(suggestions, value)
            }
            component="span"
            conjunction={conjunction}
            {...TypographyProps}
          />
        );
      })}
      {items.length > 1 && (
        <ConjunctionText {...TypographyProps}>)</ConjunctionText>
      )}
    </>
  );
};

interface OperatorValueProps extends TypographyProps {
  index: number;
  type: 'report' | 'code';
  value: string;
  conjunction: string;
}
const OperatorValue = ({
  index,
  type,
  value,
  conjunction,
  ...TypographyProps
}: OperatorValueProps) => {
  return (
    <>
      {index > 0 && (
        <ConjunctionText {...TypographyProps}> {conjunction} </ConjunctionText>
      )}
      {type === 'report' && (
        <ReportNameTypography {...TypographyProps}>
          {value}
        </ReportNameTypography>
      )}
      {type === 'code' && (
        <Code component="span" {...TypographyProps}>
          {value}
        </Code>
      )}
    </>
  );
};

export const ReportNameTypography = ({
  children,
  ...TypographyProps
}: PropsWithChildren<TypographyProps>) => {
  return (
    <Typography fontWeight={'bold'} color="black" {...TypographyProps}>
      {children}
    </Typography>
  );
};

const ConjunctionText = ({
  children,
  ...TypographyProps
}: PropsWithChildren<TypographyProps>) => {
  return (
    <Typography
      fontWeight={'light'}
      color="GrayText"
      component="span"
      {...TypographyProps}>
      {children}
    </Typography>
  );
};

// props: TypographyProps) => <Typography variant="body2" {...props} />

const Container = styled('span')`
  word-break: break-word;
`;

const Code = styled(Typography)`
  display: inline-block;
  border-radius: 3px;
  font-weight: 400;
  padding: 0.05em 0.3em;
  margin-top: 9px;
  background-color: rgba(0, 0, 0, 0.05);
  color: inherit;
  font-family: courier, monospace;
  caret-color: #c62828 !important;
  color: #c62828 !important;
`;

const getReportNameByUuid = (
  suggestions: Suggestions.Suggestion[],
  uuid: string
): string => {
  return suggestions.find((suggestion) => suggestion.uuid === uuid)?.name || '';
};
